import { useRef, useState } from "react";
import { UploadCloud, Camera, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const UploadZone = ({ onAnalyze, loading }) => {
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    onAnalyze(file);
  };

  const openFile = () => {
    if (!loading) fileRef.current?.click();
  };

  return (
    <div
      data-testid="upload-zone"
      role="button"
      tabIndex={0}
      onClick={openFile}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openFile()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      className={`group relative h-full w-full cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed p-10 outline-none transition-colors duration-200 sm:p-16 ${
        dragOver
          ? "border-primary bg-primary/10"
          : "border-primary/40 bg-card hover:border-primary/70 hover:bg-primary/[0.04]"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 grain-texture opacity-40" aria-hidden="true" />
      <div className="relative flex h-full flex-col items-center justify-center text-center">
        <div className="relative mb-6 flex h-20 w-20 items-center justify-center">
          <span
            className={`absolute inset-0 rounded-full bg-primary/15 transition-transform duration-300 ${
              dragOver ? "scale-110" : "group-hover:scale-105"
            }`}
            aria-hidden="true"
          />
          <span className="absolute inset-0 rounded-full border border-primary/30" aria-hidden="true" />
          <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_6px_20px_-4px_rgba(234,179,8,0.6)]">
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            ) : (
              <UploadCloud className="h-7 w-7" aria-hidden="true" />
            )}
          </div>
        </div>

        <h2 className="font-heading text-2xl font-700 uppercase tracking-wide text-foreground sm:text-3xl">
          {loading ? "Analyzing your contract…" : "Drop your Bill of Sale here"}
        </h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          {loading
            ? "This is a thorough scan and usually runs a few minutes. Please keep this tab open."
            : ""}
        </p>
        {!loading && (
          <p className="mt-1 font-mono-plex text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70">
            PDF · JPG · PNG · we never store your contract
          </p>
        )}

        {fileName && !loading && (
          <div className="mt-4 flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-1.5">
            <FileText className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="font-mono-plex text-xs text-muted-foreground">{fileName}</span>
          </div>
        )}

        <div className="mt-7 flex w-full flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            data-testid="browse-file-btn"
            disabled={loading}
            onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
            className="w-full rounded-full px-6 font-600 sm:w-auto"
          >
            <FileText className="mr-2 h-4 w-4" aria-hidden="true" /> Upload File
          </Button>
          <Button
            data-testid="camera-capture-btn"
            disabled={loading}
            onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
            className="w-full rounded-full px-6 font-600 sm:w-auto"
          >
            <Camera className="mr-2 h-4 w-4" aria-hidden="true" /> Use Camera
          </Button>
        </div>

        <input
          ref={fileRef}
          data-testid="file-input"
          type="file"
          accept="image/png,image/jpeg,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <input
          ref={cameraRef}
          data-testid="camera-input"
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};
